import React from "react";

type LongPressButtonProps = {
    onConfirm: () => void;
    duration?: number;
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
};

const LongPressButton = ({ onConfirm, duration = 800, className, style, children }: LongPressButtonProps) => {
    const [progress, setProgress] = React.useState(0);
    const rafRef = React.useRef<number | null>(null);
    const startTimeRef = React.useRef<number>(0);
    const confirmedRef = React.useRef(false);

    const start = () => {
        startTimeRef.current = performance.now();
        confirmedRef.current = false;

        const loop = (now: number) => {
            const elapsed = now - startTimeRef.current;
            const p = Math.min(elapsed / duration, 1);
            setProgress(p);

            if(p >= 1 && !confirmedRef.current) {
                confirmedRef.current = true;
                onConfirm();
                return;
            }

            rafRef.current = requestAnimationFrame(loop);
        };

        rafRef.current = requestAnimationFrame(loop);
    };

    const cancel = () => {
        if(rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }

        setProgress(0);
    };

    return (
        <button
            onMouseDown={start}
            onMouseUp={cancel}
            onMouseLeave={cancel}
            onTouchStart={start}
            onTouchEnd={cancel}
            className={`relative overflow-hidden ${className || ""}`}
            style={style}
        >
            <div
                className="absolute inset-0 bg-black/10 origin-left"
                style={{ transform: `scaleX(${progress})`, transition: progress === 0 ? "transform 0.2s" : "none" }}
            />
            <span className="relative">{children}</span>
        </button>
    );
};

export default LongPressButton;
